import { useEffect, useState } from "react";
import { api, formatApiError } from "../api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";

const PAGES = {
  home: [
    { key: "hero_title", label: "Hero title", type: "text" },
    { key: "hero_subtitle", label: "Hero subtitle", type: "textarea" },
    { key: "hero_cta_label", label: "Hero CTA label", type: "text" },
    { key: "hero_cta_link", label: "Hero CTA link", type: "text" },
    { key: "mission_preview", label: "Mission preview", type: "textarea" },
  ],
  about: [
    { key: "mission", label: "Mission statement", type: "textarea" },
    { key: "vision", label: "Vision", type: "textarea" },
    { key: "history", label: "History", type: "textarea" },
  ],
  contact: [
    { key: "heading", label: "Page heading", type: "text" },
    { key: "intro", label: "Intro paragraph", type: "textarea" },
  ],
};

function PageEditor({ page }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data: d } = await api.get(`/api/admin/content/${page}`);
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    setData(null);
    load();
    // eslint-disable-next-line
  }, [page]);

  const save = async () => {
    setSaving(true);
    try {
      const fields = PAGES[page];
      const payload = {};
      fields.forEach((f) => (payload[f.key] = data[f.key] ?? ""));
      const { data: updated } = await api.put(`/api/admin/content/${page}`, payload);
      setData(updated);
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!data)
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
      </div>
    );

  return (
    <Card className="p-6 space-y-4" data-testid={`content-editor-${page}`}>
      {PAGES[page].map((f) => (
        <div key={f.key}>
          <Label>{f.label}</Label>
          {f.type === "textarea" ? (
            <Textarea
              rows={3}
              value={data[f.key] || ""}
              onChange={(e) => setData({ ...data, [f.key]: e.target.value })}
              data-testid={`content-${page}-${f.key}`}
            />
          ) : (
            <Input
              value={data[f.key] || ""}
              onChange={(e) => setData({ ...data, [f.key]: e.target.value })}
              data-testid={`content-${page}-${f.key}`}
            />
          )}
        </div>
      ))}
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-primary hover:bg-primary-dark text-white" data-testid={`save-content-${page}`}>
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save
        </Button>
      </div>
    </Card>
  );
}

export default function PageContent() {
  const [tab, setTab] = useState("home");
  return (
    <div className="max-w-4xl space-y-6" data-testid="page-content-page">
      <div>
        <h1 className="text-3xl font-serif font-bold">Page Content</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Edit the text snippets shown on each public page. Changes go live immediately.
        </p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="home" data-testid="tab-home">Home</TabsTrigger>
          <TabsTrigger value="about" data-testid="tab-about">About</TabsTrigger>
          <TabsTrigger value="contact" data-testid="tab-contact">Contact</TabsTrigger>
        </TabsList>
        <TabsContent value="home"><PageEditor page="home" /></TabsContent>
        <TabsContent value="about"><PageEditor page="about" /></TabsContent>
        <TabsContent value="contact"><PageEditor page="contact" /></TabsContent>
      </Tabs>
    </div>
  );
}
